import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon, 
  Clock, 
  User, 
  Activity,
  X,
  Plus,
  Loader2,
  CalendarCheck,
  Video,
  MapPin,
  Stethoscope,
  FileText,
  ExternalLink,
  Check
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';

const AppointmentOverview = () => {
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedVisitType, setSelectedVisitType] = useState('outpatient');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchAppointments();
  }, [selectedDate]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${window.location.protocol}//${window.location.hostname}/api/appointments`, { headers });
      const physicalApps = response.data.filter(a => a.isVirtual !== true);
      setAppointments(physicalApps);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getWeekDays = (date) => {
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay() + (start.getDay() === 0 ? -6 : 1)); // Start from Monday
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  };

  const weekDays = getWeekDays(selectedDate);
  const timeSlots = Array.from({ length: 15 }, (_, i) => `${(i + 8).toString().padStart(2, '0')}:00`); 

  const handlePrevWeek = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 7);
    setSelectedDate(d);
  };

  const handleNextWeek = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 7);
    setSelectedDate(d);
  };

  const openAppointment = (appt) => {
    setSelectedAppointment(appt);
    setShowModal(true);
  };

  if (loading && appointments.length === 0) return (
    <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
    </div>
  );

  return (
    <div className="p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700">
      <div className="max-w-full mx-auto space-y-4">
        
        {/* Cinematic Header */}
        <div className="bg-white/80 backdrop-blur-md px-8 py-5 rounded-[24px] border border-slate-200 shadow-xl shadow-slate-200/30 flex justify-between items-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-indigo-500 via-sky-500 to-indigo-500" />
            <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-200">
                    <CalendarCheck className="w-5 h-5 text-white" />
                </div>
                <div>
                    <h1 className="text-xl font-black text-slate-800 tracking-tight">Clinical Calendar</h1>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Real-time Appointment Matrix</p>
                </div>
            </div>

            <div className="flex items-center gap-3">
                <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl p-1 shadow-inner">
                    <button onClick={handlePrevWeek} className="p-1.5 hover:bg-white hover:shadow-sm rounded-lg transition-all text-slate-500">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button 
                        onClick={() => setSelectedDate(new Date())}
                        className="px-5 py-1.5 text-[10px] font-black text-slate-700 uppercase tracking-widest hover:text-indigo-600 transition-colors"
                    >
                        Today
                    </button>
                    <button onClick={handleNextWeek} className="p-1.5 hover:bg-white hover:shadow-sm rounded-lg transition-all text-slate-500">
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
                
                <h2 className="text-lg font-black text-slate-800 tracking-tight min-w-[150px] text-center">
                    {weekDays[0].toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </h2>

                <div className="h-8 w-[1px] bg-slate-100 mx-2" />

                <Link 
                    to="/dashboard/patient-admin/appointments/book-doctor-appointment" 
                    className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-black text-white rounded-xl text-[11px] font-black uppercase tracking-widest shadow-xl shadow-indigo-100 transition-all active:scale-95 group"
                >
                    <Plus className="w-3.5 h-3.5 group-hover:rotate-90 transition-transform" />
                    Book New
                </Link>
            </div>
        </div>

        {/* Compact Calendar Grid */}
        <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden flex flex-col h-[calc(100vh-160px)]">
          <div className="flex-grow overflow-auto custom-scrollbar">
            <div className="min-w-[1100px] h-full flex flex-col">
              
              {/* Day Titles */}
              <div className="grid grid-cols-[80px_repeat(7,1fr)] border-b border-slate-100 sticky top-0 z-10 bg-white">
                <div className="p-4 text-[10px] font-black text-slate-400 uppercase tracking-widest border-r border-slate-50 flex items-center justify-center">Time Matrix</div>
                {weekDays.map((day, idx) => {
                  const isToday = day.toDateString() === new Date().toDateString();
                  return (
                    <div key={idx} className={`p-4 text-center border-r border-slate-50 last:border-0 relative ${isToday ? 'bg-indigo-50/20' : ''}`}>
                      <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">
                        {day.toLocaleDateString('en-US', { weekday: 'short' })}
                      </div>
                      <div className={`text-xl font-black ${isToday ? 'text-indigo-600' : 'text-slate-800'}`}>
                        {day.getDate().toString().padStart(2, '0')}
                      </div>
                      {isToday && <div className="absolute top-0 left-0 w-full h-[2px] bg-indigo-500 shadow-[0_0_8px_rgba(79,70,229,0.5)]" />}
                    </div>
                  );
                })}
              </div>

              {/* Matrix Cells */}
              <div className="flex-grow relative bg-slate-50/10">
                {timeSlots.map((time, tIdx) => (
                  <div key={tIdx} className="grid grid-cols-[80px_repeat(7,1fr)] group min-h-[50px] border-b border-slate-50/50">
                    <div className="p-2 text-[10px] font-black text-slate-300 border-r border-slate-50 flex justify-center items-center font-mono group-hover:text-indigo-400 transition-colors">
                      {time}
                    </div>
                    {weekDays.map((day, dIdx) => {
                      const cellDateStr = day.toISOString().split('T')[0];
                      const cellApps = appointments.filter(a => {
                         const start = new Date(a.startTime);
                         return start.toISOString().split('T')[0] === cellDateStr && 
                                start.getHours().toString().padStart(2, '0') === time.split(':')[0];
                      });

                      return (
                        <div key={dIdx} className="border-r border-slate-50/50 last:border-0 relative hover:bg-white/80 transition-all p-1.5 min-h-[60px]">
                          {cellApps.map((appt, aIdx) => (
                            <div 
                              key={aIdx}
                              onClick={() => openAppointment(appt)}
                              className={`rounded-xl p-3 text-[11px] mb-1.5 transition-all cursor-pointer relative overflow-hidden group/appt border-l-4 hover:scale-[1.03] hover:shadow-xl shadow-sm ${
                                  appt.isVirtual 
                                  ? 'bg-purple-600 border-purple-400 text-white shadow-purple-100' 
                                  : 'bg-indigo-600 border-indigo-400 text-white shadow-indigo-100'
                              }`}
                            >
                                <div className="font-black truncate text-[12px] pr-4 flex items-center gap-1.5">
                                  {appt.patientName || `${appt.Patient?.firstName} ${appt.Patient?.lastName}`}
                                  {appt.status === 'checked-in' && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)] shrink-0 animate-pulse" />}
                                </div>
                              <div className="flex items-center gap-1.5 mt-1 text-[9px] font-black uppercase tracking-tighter opacity-80 whitespace-nowrap">
                                <Stethoscope className="w-2.5 h-2.5 shrink-0" />
                                <span className="truncate">{appt.doctorName || appt.Doctor?.username}</span>
                              </div>
                              <div className="flex items-center gap-1.5 mt-1.5">
                                 <span className="px-1.5 py-0.5 bg-white/20 rounded-md text-[8px] font-black uppercase tracking-widest shrink-0">
                                    {appt.AppointmentType?.typeName || 'General'}
                                 </span>
                                 {appt.isVirtual && <Video className="w-3 h-3 text-white/90" />}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Appointment Detail Modal (Enhanced) */}
      {showModal && selectedAppointment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-[40px] w-full max-w-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 relative border border-slate-100">
            
            {/* Modal Hero Header */}
            <div className={`p-10 relative overflow-hidden ${selectedAppointment.isVirtual ? 'bg-purple-600' : 'bg-indigo-600'}`}>
                <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
                <button 
                    onClick={() => setShowModal(false)}
                    className="absolute top-8 right-8 p-3 bg-white/15 hover:bg-white/30 rounded-2xl transition-all text-white backdrop-blur-sm"
                >
                    <X className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-6 relative z-10">
                    <div className="w-20 h-20 bg-white shadow-2xl shadow-black/20 rounded-[28px] flex items-center justify-center text-3xl font-black text-indigo-600 border-4 border-white/20 shrink-0">
                        {String(selectedAppointment.patientName || selectedAppointment.Patient?.firstName)[0]}
                    </div>
                    <div>
                        <h3 className="text-3xl font-black text-white tracking-tight leading-tight">
                            {selectedAppointment.patientName || `${selectedAppointment.Patient?.firstName} ${selectedAppointment.Patient?.lastName}`}
                        </h3>
                        <div className="flex items-center gap-4 mt-2">
                             <div className="flex items-center gap-1.5 text-indigo-100 font-bold text-sm">
                                <CalendarIcon className="w-4 h-4 text-white/70" />
                                {new Date(selectedAppointment.startTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                             </div>
                             <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
                             <div className="flex items-center gap-1.5 text-indigo-100 font-bold text-sm uppercase tracking-widest text-[11px]">
                                <Clock className="w-4 h-4 text-white/70" />
                                {new Date(selectedAppointment.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                             </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="p-10 space-y-8">
                {/* Meta Grid */}
                <div className="grid grid-cols-2 gap-8">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                            <Stethoscope className="w-3 h-3" /> Practitioner
                        </label>
                        <div className="text-slate-800 font-black text-lg leading-tight uppercase tracking-tight">
                            {selectedAppointment.doctorName || selectedAppointment.Doctor?.username}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                            <Activity className="w-3 h-3" /> Session Type
                        </label>
                        <div className="inline-flex px-3 py-1 bg-indigo-50 text-indigo-600 rounded-lg font-black text-[12px] uppercase tracking-wide border border-indigo-100">
                             {selectedAppointment.AppointmentType?.typeName || 'Medical Review'}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-8 border-t border-slate-50 pt-8">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                            <MapPin className="w-3 h-3" /> Location / Room
                        </label>
                        <div className="flex items-center gap-2 font-bold text-slate-700 text-sm">
                            {selectedAppointment.isVirtual ? (
                                <span className="bg-purple-50 text-purple-600 px-3 py-1 rounded-lg text-xs font-black uppercase flex items-center gap-2 tracking-widest">
                                    <Video className="w-3.5 h-3.5" /> Virtual Telemedicine
                                </span>
                            ) : (
                                <span>{selectedAppointment.location || 'Consultation Room'}</span>
                            )}
                        </div>
                    </div>
                    <div className="space-y-2 text-right">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Clinic Branch</label>
                        <div className="text-slate-500 font-black text-xs uppercase tracking-widest">
                            {selectedAppointment.branch || 'Main Medical Center'}
                        </div>
                    </div>
                </div>

                {selectedAppointment.notes && (
                    <div className="bg-slate-50 p-6 rounded-[24px] border border-slate-200/50 italic text-slate-500 text-sm font-medium leading-relaxed relative">
                        <FileText className="absolute -top-3 -left-3 w-8 h-8 text-indigo-100" />
                        "{selectedAppointment.notes}"
                    </div>
                )}

                <div className="flex flex-col gap-4 pt-4">
                    <div className="grid grid-cols-4 gap-2">
                        {[
                            { id: 'outpatient', label: 'OPD' },
                            { id: 'emergency', label: 'ETU' },
                            { id: 'inpatient', label: 'IPD' },
                            { id: 'mortuary', label: 'Mortuary' }
                        ].map(type => (
                            <button 
                                key={type.id}
                                onClick={() => setSelectedVisitType(type.id)}
                                className={`py-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all border ${
                                    selectedVisitType === type.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-50 text-slate-400 border-slate-100 hover:border-slate-200'
                                }`}
                            >
                                {type.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-4">
                        <button className="flex-1 py-4 bg-slate-100 hover:bg-slate-200 text-slate-600 font-black text-[11px] uppercase tracking-widest rounded-2xl transition-all flex items-center justify-center gap-2 active:scale-95">
                            <ExternalLink className="w-4 h-4" /> Edit Details
                        </button>
                        <button 
                            disabled={selectedAppointment.status === 'checked-in'}
                            onClick={async () => {
                                try {
                                    const userId = localStorage.getItem('userId');
                                    await axios.post(`http://localhost:5000/api/appointments/${selectedAppointment.id}/check-in`, {
                                        visitType: selectedVisitType,
                                        createdBy: userId
                                    }, { headers });
                                    toast.success('Patient Checked-in Successfully');
                                    setShowModal(false);
                                    fetchAppointments();
                                } catch (err) {
                                    toast.error('Check-in Failed');
                                }
                            }}
                            className={`flex-1 py-4 font-black text-[11px] uppercase tracking-widest rounded-2xl shadow-xl transition-all flex items-center justify-center gap-2 active:scale-95 ${
                                selectedAppointment.status === 'checked-in' 
                                ? 'bg-emerald-50 text-emerald-600 cursor-not-allowed shadow-none' 
                                : 'bg-indigo-600 hover:bg-black text-white shadow-indigo-100'
                            }`}
                        >
                            {selectedAppointment.status === 'checked-in' ? (
                                <><Check className="w-4 h-4" /> Already In</>
                            ) : (
                                <><MapPin className="w-4 h-4" /> Confirm Check-in</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentOverview;
